import React from "react";
import { Col, Row } from "react-bootstrap";
import styled from "styled-components";
import {
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
} from "../../common/colors";
import { observeResize } from "../../common/utils";
import { LabelWithTooltip } from "./DashboardStyles";

const Card = styled.div`
  height: 100%;
  padding: 18px 20px 20px;
  border-radius: 20px;
  background-color: ${COLOR_SURFACE_CONTAINER};
`;

const MetricLabel = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const MetricValue = styled.div`
  margin-top: 10px;
  font-size: clamp(30px, 7vw, 44px);
  font-weight: 600;
  line-height: 1.1;
  color: ${COLOR_TEXT_PRIMARY};
  white-space: nowrap;
`;

const fitValuesToWidth = (row: HTMLElement) => {
  const values = Array.from(
    row.querySelectorAll<HTMLElement>("[data-metric-value]")
  );
  values.forEach((value) => {
    value.style.fontSize = "";
  });
  const scale = Math.min(
    1,
    ...values.map((value) => value.clientWidth / value.scrollWidth)
  );
  if (scale < 1) {
    const fontSize = parseFloat(getComputedStyle(values[0]).fontSize);
    values.forEach((value) => {
      value.style.fontSize = `${Math.floor(fontSize * scale)}px`;
    });
  }
};

export const MetricsRow = ({ children }: { children: React.ReactNode }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const row = ref.current;
    if (!row) {
      return;
    }
    let fittedWidth = row.clientWidth;
    fitValuesToWidth(row);
    if (document.fonts.status !== "loaded") {
      document.fonts.ready.then(() => fitValuesToWidth(row));
    }
    const valuesObserver = new MutationObserver(() => fitValuesToWidth(row));
    valuesObserver.observe(row, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    const stopObservingResize = observeResize([row], () => {
      if (row.clientWidth !== fittedWidth) {
        fittedWidth = row.clientWidth;
        fitValuesToWidth(row);
      }
    });
    return () => {
      valuesObserver.disconnect();
      stopObservingResize();
    };
  }, []);

  return (
    <Row ref={ref} className="g-3 g-sm-4">
      {children}
    </Row>
  );
};

export const MetricsCard = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  // Explains what the number is scoped to, for cards where that isn't obvious from the label.
  tooltip?: string;
}) => {
  return (
    <Col xs={6} xl={3}>
      <Card>
        <MetricLabel>
          {tooltip ? (
            <LabelWithTooltip tooltip={tooltip}>{label}</LabelWithTooltip>
          ) : (
            label
          )}
        </MetricLabel>
        <MetricValue data-metric-value>{value}</MetricValue>
      </Card>
    </Col>
  );
};
